import React, { useState } from "react";
import { ScrollView, StyleSheet, View, Text } from "react-native";
import { B, H1, H2, H3, LI, P } from "@expo/html-elements";
import { CuratorHeader } from "../components/curator/CuratorHeader";
import { smBreakpoint, lgBreakpoint } from "../constants";
import { useThemeState } from "../context/ThemeContext";
import {
  GTAmericaCompressed,
  mdbreakpoint,
  GTAmerica,
  PanamaBold,
} from "../libs/constants";
import { useDimensions } from "../context/DimensionsContext";
import { TouchableOpacity } from "../components/common/TouchableOpacity";
import { ImageCarousel } from "../components/common/ImageCarousel";
import { TextureUvs } from "pixi.js";

interface Props { }

export const PotteryScreen: React.FC<Props> = () => {
  document.title = "The Pottery | Happy Medium";

  const { theme } = useThemeState();
  const { width, height } = useDimensions();
  const styles = StyleSheet.create({
    text: {
      color: theme.foregroundColor,
    },
    secondaryHeader: {
      fontFamily: GTAmericaCompressed,
      textTransform: "uppercase",
      color: theme.foregroundColor,
      fontSize: width < smBreakpoint ? 20 : width < mdbreakpoint ? 24 : 28,
    },
    body: {
      fontFamily: GTAmerica,
      fontWeight: "300",
      fontSize: width < smBreakpoint ? 16 : width < mdbreakpoint ? 16 : 18,
      letterSpacing: 1.07,
    },
    infoText: {
      fontFamily: GTAmericaCompressed,
      fontSize: width < smBreakpoint ? 14 : width < mdbreakpoint ? 16 : 18,
      letterSpacing: 1.02,
    },
    header: {
      fontFamily: PanamaBold,
      color: theme.foregroundColor,
      textTransform: "uppercase",
      alignSelf: "center",
      fontSize: width < smBreakpoint ? 34 : width < mdbreakpoint ? 42 : 48,
      letterSpacing: 1.02,
      lineHeight: width < smBreakpoint ? 38 : width < mdbreakpoint ? 48 : 54,
      textAlign: "center",
    },
    h2: {
      fontSize: width < smBreakpoint ? 28 : width < mdbreakpoint ? 34 : 40,
    },
    h3: {
      fontSize: width < smBreakpoint ? 22 : width < mdbreakpoint ? 24 : 28,
    },
    pushButton: {
      borderWidth: 1,
      borderColor: theme.foregroundColor,
      borderRadius: 5,
      // padding: 5,
      justifyContent: "center",
      height: 100,
      // width: 222,
      marginHorizontal: width < mdbreakpoint ? 0 : 5,
      marginVertical: 5,
    },
    timeSlot: { width: width < lgBreakpoint ? 140 : 222 },
    item: { width: 222 },
  });

  type Experience = "Wheel" | "HB" | "OW";
  type Time = "1" | "4" | "7" | "10" | "3" | "6" | "9";
  type Day = '7/18' | '7/19' | '7/20' | '7/25' | '7/26' | '7/27' | '8/1' | '8/2' | '8/3' | '8/8' | '8/9' | '8/10' | '8/15' | '8/16' | '8/17' | '8/22' | '8/23' | '8/24' | '8/29' | '8/30' | '8/31';
  type Slot = { time: Time; day: Day };
  type PaymentLink = {
    experience: Experience;
    // time: Time;
    // day: Day;
    slot: Slot;
    paymentLink: string;
    price: string;
    isAvailable?: boolean;
  };



  const [selectedExperience, setSelectedExperience] = useState<Experience>("Wheel");

  const soldOutTimeSlots: Slot[] = [
  ];

  const containsObject = (obj: Slot, list: Slot[]) => {
    return list.some((e) => JSON.stringify(e) === JSON.stringify(obj));
  };

  const data = [
    {
      product: "Wheel",
      name: "Wheel Throwing Class",
      description:
        "This low-key workout is a full body exercise in calming the mind and controlling the hands. Harness centrifugal force to squeeze, lift and shape a mound of  clay into the vessel of your dreams. If you're new to this, the objective is to make as many pots as you can in 2 hours. You'll get better with time. Good for: pots, cups, bowls, small vases, and ashtrays with uniform shape.",
      id: "wheeeeel",
      assets: [
        {
          id: "w1",
          url: "",
          height: 100,
          width: 100,
          caption: "",
        },
      ],
    },
    {
      product: "HB",
      name: "Hand Building Class",
      description:
        "A more controlled approach to pottery, hand building is like table-top sculpting. Use your hands, carving tools, and coiling techniques to shape the clay into anything you want. Good for: ashtrays, figurines, tiles, and cups with intricate details.",
      id: "hand",
      assets: [
        {
          id: "h1",
          url: "",
          height: 100,
          width: 100,
          caption: "",
        },
      ],
    },
    {
      product: "OW",
      name: "Open Wheel Session",
      description:
        "Designed for those who have taken our Wheel Class and want to get more practice time in. This is an uninstructed, two-hour time slot in The Pottery to get your reps in. This session only includes 5lbs of clay - firing services & more clay available in the studio for an additional fee. You must take our Wheel Experience class first, regardless of previous experience!",
      id: "open",
      assets: [
        {
          id: "h1",
          url: "",
          height: 100,
          width: 100,
          caption: "",
        },
      ],
    },
  ];

  const paymentLinks: PaymentLink[] = [

    {
      experience: "Wheel",
      slot: { day: "7/25", time: "4" },
      paymentLink: "https://buy.stripe.com/aFaaEZa765cB10y4gU9R60z",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "7/25", time: "7" },
      paymentLink: "https://buy.stripe.com/28EeVf93248x10ycNq9R60A",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "7/26", time: "1" },
      paymentLink: "https://buy.stripe.com/dRmcN74MM20p24C7t69R60B",
      price: "$120",
      isAvailable: false,
    },
    {
      experience: "Wheel",
      slot: { day: "7/26", time: "4" },
      paymentLink: "https://buy.stripe.com/aFa8wRgvu5cBbFccNq9R60C",
      price: "$120",
      isAvailable: true,
    },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/28E00l0wweNbcJg9Be9R60D",
    //   price: "$120",
    //   isAvailable: true,
    // },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/14A9AVgvu7kJeRoaFi9R60E",
    //   price: "$120",
    //   isAvailable: true,
    // },
    {
      experience: "Wheel",
      slot: { day: "7/27", time: "1" },
      paymentLink: "https://buy.stripe.com/eVqfZjenm6gFeRoaFi9R60F",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "7/27", time: "4" },
      paymentLink: "https://buy.stripe.com/5kQbJ36UU9sRdNk9Be9R60G",
      price: "$120",
      isAvailable: true,
    },

    {
      experience: "HB",
      slot: { day: "7/25", time: "4" },
      paymentLink: "https://buy.stripe.com/7sYcN74MMcF3eRo28M9R615",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "7/25", time: "7" },
      paymentLink: "https://buy.stripe.com/9B68wRenm8oN7oWfZC9R616",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "7/26", time: "1" },
      paymentLink: "https://buy.stripe.com/14AdRbgvu5cBdNkcNq9R617",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "7/26", time: "4" },
      paymentLink: "https://buy.stripe.com/00w00l5QQeNbcJg9Be9R618",
      price: "$68",
      isAvailable: true,
    },
    // {
    //   experience: "HB",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/dRm9AVbbaawV8t028M9R619",
    //   price: "$68",
    //   isAvailable: true,
    // },
    // {
    //   experience: "HB",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/6oUdRb6UU7kJ9x400E9R61a",
    //   price: "$68",
    //   isAvailable: true,
    // },
    {
      experience: "HB",
      slot: { day: "7/27", time: "1" },
      paymentLink: "https://buy.stripe.com/dRmaEZ6UU20p24C4gU9R61b",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "7/27", time: "4" },
      paymentLink: "https://buy.stripe.com/bJefZjgvubAZfVs9Be9R61c",
      price: "$68",
      isAvailable: true,
    },

    {
      experience: "OW",
      slot: { day: "7/26", time: "10" },
      paymentLink: "https://buy.stripe.com/9B63cxenmdJ7cJgaFi9R61k",
      price: "$55",
      isAvailable: true,
    },
    {
      experience: "OW",
      slot: { day: "7/27", time: "7" },
      paymentLink: "https://buy.stripe.com/14AfZjfrq9sRgZwbJm9R61l",
      price: "$55",
      isAvailable: true,
    },


    {
      experience: "Wheel",
      slot: { day: "8/1", time: "4" },
      paymentLink: "https://buy.stripe.com/8x2bJ36UUawV9x48xa9R61J",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/1", time: "7" },
      paymentLink: "https://buy.stripe.com/5kQ5kFbbabAZcJg00E9R61K",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/2", time: "1" },
      paymentLink: "https://buy.stripe.com/cNi7sN2EEcF35gO28M9R61L",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/2", time: "4" },
      paymentLink: "https://buy.stripe.com/3cIeVfbba9sRcJg14I9R61M",
      price: "$120",
      isAvailable: true,
    },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/28E00l0wweNbcJg9Be9R60D",
    //   price: "$120",
    //   isAvailable: true,
    // },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/14A9AVgvu7kJeRoaFi9R60E",
    //   price: "$120",
    //   isAvailable: true,
    // },
    {
      experience: "Wheel",
      slot: { day: "8/3", time: "1" },
      paymentLink: "https://buy.stripe.com/bJebJ3enmbAZ4cK5kY9R61N",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/3", time: "4" },
      paymentLink: "https://buy.stripe.com/8x2cN71AAgVj8t09Be9R61O",
      price: "$120",
      isAvailable: true,
    },

    {
      experience: "HB",
      slot: { day: "8/1", time: "4" },
      paymentLink: "https://buy.stripe.com/9B628tcfe34taB8eVy9R62d",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/1", time: "7" },
      paymentLink: "https://buy.stripe.com/6oUaEZ1AA48xcJg9Be9R62e",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/2", time: "1" },
      paymentLink: "https://buy.stripe.com/7sYaEZ7YYeNb5gO3cQ9R62f",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/2", time: "4" },
      paymentLink: "https://buy.stripe.com/28E6oJbba9sR8t0dRu9R62g",
      price: "$68",
      isAvailable: true,
    },
    // {
    //   experience: "HB",
    //   slot: { day: "8/2", time: "7" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    // {
    //   experience: "HB",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    {
      experience: "HB",
      slot: { day: "8/3", time: "1" },
      paymentLink: "https://buy.stripe.com/aFaeVf2EEdJ7fVs4gU9R62h",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/3", time: "4" },
      paymentLink: "https://buy.stripe.com/00w7sN4MMbAZfVsfZC9R62i",
      price: "$68",
      isAvailable: true,
    },

    {
      experience: "OW",
      slot: { day: "8/2", time: "10" },
      paymentLink: "https://buy.stripe.com/7sY5kF5QQ0Wl6kS14I9R62H",
      price: "$55",
      isAvailable: true,
    },
    {
      experience: "OW",
      slot: { day: "8/3", time: "7" },
      paymentLink: "https://buy.stripe.com/00w7sN0wwcF3cJgaFi9R62I",
      price: "$55",
      isAvailable: true,
    },


    {
      experience: "Wheel",
      slot: { day: "8/8", time: "4" },
      paymentLink: "https://buy.stripe.com/eVq00l1AA8oNdNk3cQ9R61P",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/8", time: "7" },
      paymentLink: "https://buy.stripe.com/cNi14p7YY48x24CcNq9R61Q",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/9", time: "1" },
      paymentLink: "https://buy.stripe.com/aFa14pfrq34tdNk4gU9R61R",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/9", time: "4" },
      paymentLink: "https://buy.stripe.com/6oU4gBdji9sR38G4gU9R61S",
      price: "$120",
      isAvailable: true,
    },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/28E00l0wweNbcJg9Be9R60D",
    //   price: "$120",
    //   isAvailable: true,
    // },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/14A9AVgvu7kJeRoaFi9R60E",
    //   price: "$120",
    //   isAvailable: true,
    // },
    {
      experience: "Wheel",
      slot: { day: "8/10", time: "1" },
      paymentLink: "https://buy.stripe.com/fZu5kF6UU34t6kS5kY9R61T",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/10", time: "4" },
      paymentLink: "https://buy.stripe.com/dRmbJ393248x10y5kY9R61U",
      price: "$120",
      isAvailable: true,
    },

    {
      experience: "HB",
      slot: { day: "8/8", time: "4" },
      paymentLink: "https://buy.stripe.com/9B614pfrq7kJ4cK00E9R62j",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/8", time: "7" },
      paymentLink: "https://buy.stripe.com/fZufZjdjigVj24CbJm9R62k",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/9", time: "1" },
      paymentLink: "https://buy.stripe.com/14AfZj0ww20peRoeVy9R62l",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/9", time: "4" },
      paymentLink: "https://buy.stripe.com/aFa14p7YY6gFbFc28M9R62m",
      price: "$68",
      isAvailable: true,
    },
    // {
    //   experience: "HB",
    //   slot: { day: "8/2", time: "7" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    // {
    //   experience: "HB",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    {
      experience: "HB",
      slot: { day: "8/10", time: "1" },
      paymentLink: "https://buy.stripe.com/7sY14p4MM48xgZwfZC9R62n",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/10", time: "4" },
      paymentLink: "https://buy.stripe.com/eVqcN70ww0Wl7oW4gU9R62o",
      price: "$68",
      isAvailable: true,
    },

    {
      experience: "OW",
      slot: { day: "8/9", time: "10" },
      paymentLink: "https://buy.stripe.com/aFa8wR4MM0Wl6kS9Be9R62J",
      price: "$55",
      isAvailable: true,
    },
    {
      experience: "OW",
      slot: { day: "8/10", time: "7" },
      paymentLink: "https://buy.stripe.com/7sY8wRbba6gFdNk5kY9R62K",
      price: "$55",
      isAvailable: true,
    },


    {
      experience: "Wheel",
      slot: { day: "8/15", time: "4" },
      paymentLink: "https://buy.stripe.com/aFafZj5QQbAZ24CcNq9R61V",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/15", time: "7" },
      paymentLink: "https://buy.stripe.com/8x2bJ3932fRf10yaFi9R61W",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/16", time: "1" },
      paymentLink: "https://buy.stripe.com/dRm5kF0wweNbgZwbJm9R61X",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/16", time: "4" },
      paymentLink: "https://buy.stripe.com/3cIeVfenm6gF7oW7t69R61Y",
      price: "$120",
      isAvailable: true,
    },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/28E00l0wweNbcJg9Be9R60D",
    //   price: "$120",
    //   isAvailable: true,
    // },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/14A9AVgvu7kJeRoaFi9R60E",
    //   price: "$120",
    //   isAvailable: true,
    // },
    {
      experience: "Wheel",
      slot: { day: "8/17", time: "1" },
      paymentLink: "https://buy.stripe.com/00wdRb0ww6gF8t014I9R61Z",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/17", time: "4" },
      paymentLink: "https://buy.stripe.com/00w5kF4MM0Wl8t03cQ9R620",
      price: "$120",
      isAvailable: true,
    },

    {
      experience: "HB",
      slot: { day: "8/15", time: "4" },
      paymentLink: "https://buy.stripe.com/eVqfZjcfe34t38G6p29R62p",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/15", time: "7" },
      paymentLink: "https://buy.stripe.com/9B67sN2EE48x7oWeVy9R62q",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/16", time: "1" },
      paymentLink: "https://buy.stripe.com/00w7sNfrqfRf4cK9Be9R62r",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/16", time: "4" },
      paymentLink: "https://buy.stripe.com/cNieVf2EEfRfeRo14I9R62s",
      price: "$68",
      isAvailable: true,
    },
    // {
    //   experience: "HB",
    //   slot: { day: "8/2", time: "7" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    // {
    //   experience: "HB",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    {
      experience: "HB",
      slot: { day: "8/17", time: "1" },
      paymentLink: "https://buy.stripe.com/eVqaEZdjifRf7oW7t69R62t",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/17", time: "4" },
      paymentLink: "https://buy.stripe.com/cNi4gB0ww0WleRoeVy9R62u",
      price: "$68",
      isAvailable: true,
    },

    {
      experience: "OW",
      slot: { day: "8/16", time: "10" },
      paymentLink: "https://buy.stripe.com/00w6oJ4MM7kJ5gOaFi9R62L",
      price: "$55",
      isAvailable: true,
    },
    {
      experience: "OW",
      slot: { day: "8/17", time: "7" },
      paymentLink: "https://buy.stripe.com/7sY8wRdjidJ7gZw14I9R62M",
      price: "$55",
      isAvailable: true,
    },


    {
      experience: "Wheel",
      slot: { day: "8/22", time: "4" },
      paymentLink: "https://buy.stripe.com/bJeaEZ5QQ8oN5gO00E9R621",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/22", time: "7" },
      paymentLink: "https://buy.stripe.com/cNi7sN2EEgVjeRo6p29R622",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/23", time: "1" },
      paymentLink: "https://buy.stripe.com/4gM7sNgvugVjbFcdRu9R623",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/23", time: "4" },
      paymentLink: "https://buy.stripe.com/4gMcN71AA8oNaB828M9R624",
      price: "$120",
      isAvailable: true,
    },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/28E00l0wweNbcJg9Be9R60D",
    //   price: "$120",
    //   isAvailable: true,
    // },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/14A9AVgvu7kJeRoaFi9R60E",
    //   price: "$120",
    //   isAvailable: true,
    // },
    {
      experience: "Wheel",
      slot: { day: "8/24", time: "1" },
      paymentLink: "https://buy.stripe.com/4gM14pa766gFeRo7t69R625",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/24", time: "4" },
      paymentLink: "https://buy.stripe.com/eVqdRbenm0Wl9x4aFi9R626",
      price: "$120",
      isAvailable: true,
    },

    {
      experience: "HB",
      slot: { day: "8/22", time: "4" },
      paymentLink: "https://buy.stripe.com/14A8wRbba0WlbFc14I9R62v",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/22", time: "7" },
      paymentLink: "https://buy.stripe.com/6oUaEZ7YY48x6kS3cQ9R62w",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/23", time: "1" },
      paymentLink: "https://buy.stripe.com/00wfZj6UUcF39x43cQ9R62x",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/23", time: "4" },
      paymentLink: "https://buy.stripe.com/28E14pdji34t8t0fZC9R62y",
      price: "$68",
      isAvailable: true,
    },
    // {
    //   experience: "HB",
    //   slot: { day: "8/2", time: "7" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    // {
    //   experience: "HB",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    {
      experience: "HB",
      slot: { day: "8/24", time: "1" },
      paymentLink: "https://buy.stripe.com/14AeVfcfe6gF9x4aFi9R62z",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/24", time: "4" },
      paymentLink: "https://buy.stripe.com/3cI6oJ6UU6gF24CaFi9R62A",
      price: "$68",
      isAvailable: true,
    },

    {
      experience: "OW",
      slot: { day: "8/23", time: "10" },
      paymentLink: "https://buy.stripe.com/6oU8wR7YY20p9x47t69R62N",
      price: "$55",
      isAvailable: true,
    },
    {
      experience: "OW",
      slot: { day: "8/24", time: "7" },
      paymentLink: "https://buy.stripe.com/3cIdRbfrqbAZ7oWcNq9R62O",
      price: "$55",
      isAvailable: true,
    },


    {
      experience: "Wheel",
      slot: { day: "8/29", time: "4" },
      paymentLink: "https://buy.stripe.com/14A00l3II7kJcJg28M9R627",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/29", time: "7" },
      paymentLink: "https://buy.stripe.com/28EeVf6UUbAZ24C8xa9R628",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/30", time: "1" },
      paymentLink: "https://buy.stripe.com/cNi7sNdjiawV5gO4gU9R629",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/30", time: "4" },
      paymentLink: "https://buy.stripe.com/eVqcN76UUeNb7oW8xa9R62a",
      price: "$120",
      isAvailable: true,
    },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/26", time: "7" },
    //   paymentLink: "https://buy.stripe.com/28E00l0wweNbcJg9Be9R60D",
    //   price: "$120",
    //   isAvailable: true,
    // },
    // {
    //   experience: "Wheel",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "https://buy.stripe.com/14A9AVgvu7kJeRoaFi9R60E",
    //   price: "$120",
    //   isAvailable: true,
    // },
    {
      experience: "Wheel",
      slot: { day: "8/31", time: "1" },
      paymentLink: "https://buy.stripe.com/bJe28t7YYdJ74cK4gU9R62b",
      price: "$120",
      isAvailable: true,
    },
    {
      experience: "Wheel",
      slot: { day: "8/31", time: "4" },
      paymentLink: "https://buy.stripe.com/cNifZjfrq6gFbFcdRu9R62c",
      price: "$120",
      isAvailable: true,
    },

    {
      experience: "HB",
      slot: { day: "8/29", time: "4" },
      paymentLink: "https://buy.stripe.com/eVq5kF3IIfRf38G4gU9R62B",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/29", time: "7" },
      paymentLink: "https://buy.stripe.com/aFa14pcfecF37oWcNq9R62C",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/30", time: "1" },
      paymentLink: "https://buy.stripe.com/eVqeVf3II9sR24C4gU9R62D",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/30", time: "4" },
      paymentLink: "https://buy.stripe.com/00wdRbgvu7kJ24C4gU9R62E",
      price: "$68",
      isAvailable: true,
    },
    // {
    //   experience: "HB",
    //   slot: { day: "8/2", time: "7" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    // {
    //   experience: "HB",
    //   slot: { day: "7/27", time: "10" },
    //   paymentLink: "",
    //   price: "$68",
    //   isAvailable: true,
    // },
    {
      experience: "HB",
      slot: { day: "8/31", time: "1" },
      paymentLink: "https://buy.stripe.com/00w4gBdji8oNeRoeVy9R62F",
      price: "$68",
      isAvailable: true,
    },
    {
      experience: "HB",
      slot: { day: "8/31", time: "4" },
      paymentLink: "https://buy.stripe.com/dRm4gBcfe6gF7oWfZC9R62G",
      price: "$68",
      isAvailable: true,
    },

    {
      experience: "OW",
      slot: { day: "8/30", time: "10" },
      paymentLink: "https://buy.stripe.com/4gM9AV1AAgVj10yfZC9R62P",
      price: "$55",
      isAvailable: true,
    },
    {
      experience: "OW",
      slot: { day: "8/31", time: "7" },
      paymentLink: "https://buy.stripe.com/4gMdRbdjicF36kS7t69R62Q",
      price: "$55",
      isAvailable: true,
    },

  ];

  const firstAvailWheel = paymentLinks.filter(l => l.experience === 'Wheel' && l.isAvailable === true)[0].slot
  const [selectedTime, setSelectedTime] = useState<Time>(firstAvailWheel.time);
  const [selectedDay, setSelectedDay] = useState<Day>(firstAvailWheel.day);

  interface TimeSlotProps {
    day: Day;
    time: Time;
    slot: Slot;
    isAvailable?: boolean;
  }

  const TimeSlot = ({ day, time, slot, isAvailable = true }: TimeSlotProps) => (
    <TouchableOpacity
      disabled={!isAvailable || containsObject(slot, soldOutTimeSlots)}
      style={{ margin: 5, maxWidth: '45%' }}
      onPress={() => {
        setSelectedDay(day);
        setSelectedTime(time);
      }}
    >
      <View
        style={[
          styles.pushButton,
          styles.timeSlot,
          {
            opacity: isAvailable === false ? 0.5 : 1,
            backgroundColor:
              selectedDay === day && selectedTime === time
                ? theme.foregroundColor
                : theme.backgroundColor,
          },
        ]}
      >
        <P
          style={[
            styles.text,
            styles.body,
            {
              color:
                selectedDay === day && selectedTime === time
                  ? theme.backgroundColor
                  : theme.foregroundColor,
              textAlign: 'center',
            },
          ]}
        >
          {containsObject(slot, soldOutTimeSlots) || isAvailable === false
            ? 'Sold Out'
            : `${time}:00`}
          {/* {!isAvailable} */}
        </P>
      </View>
    </TouchableOpacity>
  );

  return (
    <>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: theme.backgroundColor,
        }}
      >
        <CuratorHeader />
        <View style={{ height: width < mdbreakpoint ? 20 : 40 }} />
        <View
          style={{
            justifyContent: 'center',
            width: 700,
            maxWidth: '90vw',
            alignSelf: 'center',
          }}
        >
          <H1 style={[styles.header, { marginBottom: 0 }]}>
            The Pottery at Happy Medium
          </H1>
          <P
            style={[
              styles.text,
              styles.body,
              {
                alignSelf: 'center',
                marginTop: 0,
                letterSpacing: 1.2,
                marginBottom: 0,
                width: 280,
                textAlign: 'center',
              },
            ]}
          >
            open every {'\n'} Friday, Saturday & Sunday{'\n'}
          </P>
          <Text
            style={[
              styles.text,
              styles.body,
              {
                alignSelf: 'center',
                textAlign: 'center',
                textTransform: 'uppercase',
                marginTop: 10,
                letterSpacing: 1,
                fontWeight: 'bold',
                fontSize: 16,
              },
            ]}
          >
            49 Market St{"\n"}Ground Floor{"\n"}NY, NY 10002
          </Text>
          {/* <P style={[styles.text, styles.body]}>
            Back soon with info and tickets! Leave your number below to be
            the first to know when info and tickets are released :)
          </P>
          <View style={{ alignSelf: 'center' }}>
            <PhoneNumberSignupForm />
          </View> */}
          <View style={{ height: 20 }} />

          <P style={[styles.text, styles.body]}>
            The Pottery is a beginner-friendly pottery studio on the first floor of Happy Medium's 49 Market St. location offering wheel throwing and hand-building classes to everyone 16 & up.{"\n\n"}Classes are 2 hours long and each ticket includes  admission for one person, a 20 minute demo + 1:1 instruction throughout, unlimited clay, one fully glaze-fired pot, and all cleanup. If you fall in love with more than one of your pieces, additional pieces are available to purchase for $25.
          </P>
          <P style={[styles.text, styles.body]}>
            We also offer additional services to fully customize your pot and get it home to you safely. Each add on is $25 and can be purchased after your class.
            <LI>
              <B>Custom design: </B>want to do something custom? Come back to the Art Cafe and paint your pot! Appointments are available once your piece is bisque fired (2-3 weeks after your class) and include unlimited glaze and 2 hours in the Art Cafe on the second floor.
            </LI>
            <LI>
              <B>Just make it a candle: </B>love your piece, but not quite sure what to do with it? Just make it a candle. Choose your scent after your class and we will hand-pour it for you before you pick it up!
            </LI>
            <LI>
              <B>Shipping: </B>Don't live in NYC? No problem. We offer shipping all around the world. US shipping is $25 and international starts at $40.
            </LI>
          </P>
          <P style={[styles.text, styles.body]}>
            Open Wheel sessions are our version of a “build-your-own” membership. They're available to anyone who has taken our wheel throwing classes and include 5lbs of clay and 2 hours on the wheel. Additional clay and firing services are available for purchase at the studio.
          </P>
          <P style={[styles.text, styles.body]}>
            Processing time for pottery is 3-4 weeks and available for pickup on Thursdays from 5:30pm-9:30pm, Fridays from 4-9pm, Saturdays from 10am-9pm and Sundays from 10am-6pm on the first floor of our studio.
          </P>
          <P style={[styles.text, styles.body, { fontSize: 16, fontWeight: 'bold' }]}>
            **Please note, our tickets are non-refundable. You must notify us at pottery@happy-medium.co at least 48 hours before your time slot in order to be eligible to reschedule. Otherwise, the full price will be forfeit. If you purchase your ticket within 48 hours of the start time it is not eligible to be rescheduled.**
          </P>
          <H2 style={[styles.header, styles.h2, { marginBottom: 0 }]}>Step 1</H2>
          <P
            style={[
              styles.text,
              styles.body,
              { alignSelf: 'center', marginTop: 0, letterSpacing: 1.2 },
            ]}
          >
            Choose your experience
          </P>
          <View
            style={{
              flexDirection: width < 800 ? 'column' : 'row',
              justifyContent: 'center',
              width: '100%',
              alignContent: 'center',
              alignSelf: 'center',
            }}
          >

            <TouchableOpacity style={{ alignSelf: 'center' }} onPress={() => setSelectedExperience('Wheel')}>
              <View
                style={[styles.pushButton, styles.item, {
                  backgroundColor:
                    selectedExperience === 'Wheel'
                      ? theme.foregroundColor
                      : theme.backgroundColor,
                }]}
              >
                <P
                  style={[
                    styles.text,
                    styles.body,
                    {
                      textAlign: 'center',
                      color:
                        selectedExperience === 'Wheel'
                          ? theme.backgroundColor
                          : theme.foregroundColor,
                    },
                  ]}
                >
                  Wheel Throwing
                </P>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={{ alignSelf: 'center' }} onPress={() => setSelectedExperience('HB')}>
              <View
                style={[styles.pushButton, styles.item, {
                  backgroundColor:
                    selectedExperience === 'HB'
                      ? theme.foregroundColor
                      : theme.backgroundColor,
                }]}
              >
                <P
                  style={[
                    styles.text,
                    styles.body,
                    {
                      textAlign: 'center',
                      color:
                        selectedExperience === 'HB'
                          ? theme.backgroundColor
                          : theme.foregroundColor,
                    },
                  ]}
                >
                  Hand Building
                </P>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={{ alignSelf: 'center' }} onPress={() => setSelectedExperience('OW')}>
              <View
                style={[styles.pushButton, styles.item, {
                  backgroundColor:
                    selectedExperience === 'OW'
                      ? theme.foregroundColor
                      : theme.backgroundColor,
                }]}
              >
                <P
                  style={[
                    styles.text,
                    styles.body,
                    {
                      textAlign: 'center',
                      color:
                        selectedExperience === 'OW'
                          ? theme.backgroundColor
                          : theme.foregroundColor,
                    },
                  ]}
                >
                  Open Wheel
                </P>
              </View>
            </TouchableOpacity>

          </View>
        </View>
        <View style={{ height: 20 }} />

        {/* <ImageCarousel assets={data.filter(i => i.product === selectedDesign)?.flatMap(i => i.assets)} /> */}

        <View
          style={{
            justifyContent: 'center',
            width: 700,
            maxWidth: '90vw',
            alignSelf: 'center',
          }}
        >
          <H1 style={[styles.header, { alignSelf: 'flex-start', marginBottom: 0 }]}>
            {data.find((i) => i.product === selectedExperience)?.name}
          </H1>
          <P style={[styles.text, styles.body]}>{data.find((i) => i.product === selectedExperience)?.description}</P>
          {/* <H2 style={[styles.header, styles.h2, { marginBottom: 0 }]}>Step 2</H2>
        <P
          style={[
            styles.text,
            styles.body,
            { alignSelf: 'center', marginTop: 0, letterSpacing: 1.2 },
          ]}
        >
          Choose your wood
        </P>
          <View style={{flexDirection: 'row', width: '100%', justifyContent: 'center'}}>
        <TouchableOpacity style={{margin: 5}} onPress={() => setSelectedWood('Douglas Fir')}>
            <View
              style={{
                borderWidth: 1,
                borderColor: theme.foregroundColor,
                backgroundColor:
                  selectedWood === 'Douglas Fir'
                    ? theme.foregroundColor
                    : theme.backgroundColor,
                borderRadius: 5,
                padding: 10,
                width: 140
              }}
            >
              <P
                style={[
                  styles.text,
                  styles.body,
                  {
                    color:
                      selectedWood === 'Douglas Fir'
                        ? theme.backgroundColor
                        : theme.foregroundColor,
                    textAlign: 'center'
                  },
                ]}
              >
                Douglas Fir{'\n'}
                <Text style={[styles.text, styles.body, {fontSize: 12, color:
                      selectedWood === 'Douglas Fir'
                        ? theme.backgroundColor
                        : theme.foregroundColor,}]}>(light)</Text>
              </P>
            </View>
          </TouchableOpacity>
        <TouchableOpacity style={{margin: 5}} onPress={() => setSelectedWood('Mahogany')}>
            <View
              style={{
                borderWidth: 1,
                borderColor: theme.foregroundColor,
                backgroundColor:
                  selectedWood === 'Mahogany'
                    ? theme.foregroundColor
                    : theme.backgroundColor,
                borderRadius: 5,
                padding: 10,
                width: 140
              }}
            >
              <P
                style={[
                  styles.text,
                  styles.body,
                  {
                    color:
                      selectedWood === 'Mahogany'
                        ? theme.backgroundColor
                        : theme.foregroundColor,
                    textAlign: 'center'
                  },
                ]}
              >
                Mahogany{'\n'}
                <Text style={[styles.text, styles.body, {fontSize: 12, color:
                      selectedWood === 'Mahogany'
                        ? theme.backgroundColor
                        : theme.foregroundColor,}]}>(dark)</Text>
              </P>
            </View>
          </TouchableOpacity> */}
        </View>
        <View style={{ height: 60 }} />
        <H2 style={[styles.header, styles.h2, { marginBottom: 0 }]}>Step 2</H2>
        <P
          style={[
            styles.text,
            styles.body,
            { textAlign: 'center', alignSelf: 'center', marginTop: 0, letterSpacing: 1.2 },
          ]}
        >
          Choose your day and time{'\n'}
          <Text style={[styles.text, styles.body, { fontSize: 12 }]}>(each experience lasts up to 2 hours)</Text>
        </P>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 7/25</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '7/25').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 7/26</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '7/26').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 7/27</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '7/27').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 8/1</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/1').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 8/2</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/2').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 8/3</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/3').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 8/8</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/8').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 8/9</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/9').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 8/10</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/10').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 8/8</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/8').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 8/9</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/9').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 8/10</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/10').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 8/15</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/15').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 8/16</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/16').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 8/17</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/17').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 8/22</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/22').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 8/23</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/23').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 8/24</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/24').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>

        <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'center', alignSelf: 'center', flexWrap: 'wrap' }}>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Friday 8/29</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/29').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Saturday 8/30</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/30').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
          <View style={{ flexDirection: 'column', maxWidth: '45%' }}>
            <H3 style={[styles.header, styles.h3, { margin: 20, marginBottom: 0 }]} >Sunday 8/31</H3>
            {paymentLinks.filter(l => l.experience === selectedExperience && l.slot.day === '8/31').map(l => {
              return <TimeSlot key={`${l.slot.day}-${l.slot.time}-${l.experience}`} slot={l.slot} day={l.slot.day} time={l.slot.time} isAvailable={l.isAvailable} />
            })}

          </View>
        </View>


        {/* </View> */}
        <View style={{ height: 40 }} />


        <View style={{ height: 60 }} />
        {/* </View> */}
      </ScrollView>

      <TouchableOpacity disabled={false} style={{}} onPress={() => window.open(paymentLinks.find(l => l.experience === selectedExperience && l.slot.day === selectedDay && l.slot.time === selectedTime)?.paymentLink, '__blank')}>
        <View style={{ position: 'absolute', bottom: 0, right: width < mdbreakpoint ? undefined : 0, backgroundColor: theme.foregroundColor, borderRadius: 50, flexDirection: 'row', justifyContent: 'space-between', padding: 15, paddingHorizontal: 60, margin: 20, width: 320, alignSelf: 'center', borderWidth: 1, borderColor: theme.backgroundColor }}>
          {/* <Text style={[styles.text, styles.h3, {color: theme.backgroundColor, textTransform: 'uppercase', fontFamily: GTAmericaCompressed, letterSpacing: 1}]}>Reserve Now</Text> */}

          <Text style={[styles.text, styles.h3, { color: theme.backgroundColor, textTransform: 'uppercase', fontFamily: GTAmericaCompressed, letterSpacing: 1 }]}>Checkout</Text>
          <Text style={[styles.text, styles.h3, { color: theme.backgroundColor, textTransform: 'uppercase', fontFamily: GTAmericaCompressed, letterSpacing: 1 }]}>{paymentLinks.find(l => l.experience === selectedExperience && l.slot.day === selectedDay && l.slot.time === selectedTime)?.price}</Text>
        </View>
      </TouchableOpacity>
    </>

  );
};
